
import React, { useState } from "react";
import { format, differenceInDays } from "date-fns";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpenCheck, Eye, Edit, Clock, Calendar } from "lucide-react";
import { Student, Program } from "@/services/db";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface StudentResultCardProps { 
  student: Student; 
  query: string; 
  highlightText: (text: string, highlight: string) => React.ReactNode;
  calculateTotalFees: (programs: { fee: number }[]) => number;
}

const StudentResultCard = ({ 
  student, 
  query, 
  highlightText,
  calculateTotalFees
}: StudentResultCardProps) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  
  // Get first letter of name for avatar fallback
  const nameInitial = student.name.charAt(0).toUpperCase();
  
  // Calculate remaining days for each program
  const getRemainingDays = (program: Program) => {
    const completionDate = new Date(program.completionDate);
    const today = new Date();
    const remainingDays = differenceInDays(completionDate, today);
    return remainingDays > 0 ? remainingDays : 0;
  };
  
  // Get fee status color
  const getFeeStatusColor = (status: string) => {
    switch(status) {
      case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'pending': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'partial': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };
  
  // Enhanced 3D effect with mouse tracking
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isHovering) return;
    
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    
    // Calculate mouse position relative to card center
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    // Normalize values (-1 to 1)
    const normalizedX = x / (rect.width / 2);
    const normalizedY = y / (rect.height / 2);
    
    setMousePosition({ x: normalizedX, y: normalizedY });
  };
  
  return (
    <Card 
      className="overflow-hidden mb-4 card-3d-container"
      style={{
        transform: isHovering ? 
          `perspective(1000px) rotateY(${mousePosition.x * 5}deg) rotateX(${-mousePosition.y * 5}deg) scale(1.02)` : 
          'perspective(1000px) rotateY(0) rotateX(0) scale(1)',
        boxShadow: isHovering ? 
          '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 20px rgba(0, 119, 119, 0.1)' : 
          '0 2px 10px rgba(0, 0, 0, 0.05)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setMousePosition({ x: 0, y: 0 });
      }}
    >
      <CardHeader className="bg-nalc-teal/10 dark:bg-nalc-teal/20 pb-2">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border-2 border-nalc-teal/20">
              <AvatarImage src={student.profileImage} alt={student.name} />
              <AvatarFallback className="bg-nalc-teal/10 text-nalc-teal text-xl font-semibold">
                {nameInitial}
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-lg">
                {highlightText(student.name, query)}
              </CardTitle>
              <CardDescription>
                Student ID: {student.id}
                {student.contactNumber && (
                  <span className="ml-2">| Contact: {student.contactNumber}</span>
                )}
              </CardDescription>
            </div>
          </div>
          <div className="space-x-2">
            <Link to={`/students/${student.id}`}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <Eye className="h-4 w-4" />
              </Button>
            </Link>
            <Link to={`/students/edit/${student.id}`}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <Edit className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium flex items-center">
              <BookOpenCheck className="mr-2 h-4 w-4 text-nalc-teal" /> Programs
            </span>
            <Badge variant="outline">{student.programs.length}</Badge>
          </div>
          
          <div className="space-y-2">
            {student.programs.map((program, index) => (
              <div key={index} className="bg-gray-50 dark:bg-nalc-dark/20 p-2 rounded-md">
                <div className="flex justify-between items-center">
                  <span className="font-medium">
                    {highlightText(program.title, query)}
                  </span>
                  <span className="font-semibold">${program.fee}</span>
                </div>
                
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-2">
                  <span>Category: {highlightText(program.category, query)}</span>
                  
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>Enrolled: {format(new Date(program.enrolledAt), "MMM d, yyyy")}</span>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>
                      {getRemainingDays(program) > 0 
                        ? `${getRemainingDays(program)} days remaining` 
                        : "Completed"}
                    </span>
                  </div>
                  
                  <span className={`px-2 py-0.5 rounded-full ${getFeeStatusColor(program.feeStatus)}`}>
                    {program.feeStatus.charAt(0).toUpperCase() + program.feeStatus.slice(1)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter className="bg-slate-50 dark:bg-nalc-dark/30 flex justify-between py-3">
        <span className="text-sm text-muted-foreground">
          Added: {format(new Date(student.createdAt), "PP")}
        </span>
        <span className="text-sm font-medium">
          Total: ${calculateTotalFees(student.programs)}
        </span>
      </CardFooter>
    </Card>
  );
};

export default StudentResultCard;
